import loaders from './loaders'
import providers from './providers'

export default function (ctx, inject) {
    const { $config, route, isDev } = ctx

    // Queue to keep track of all the integrations that we are loading.
    const integrationQueue = []

    // Queue to keep track of all the callbacks that need to be run after all the integrations have been loaded.
    const integrationCallbacks = []

    // The object of integrations that we expose to the app.
    const integrations = {}

    // Adds an integration to the queue. This will begin adding the integration as soon as the function is called
    const addIntegration = (integrationOptions) => {
        const integration = createIntegration(integrationOptions)
        integrationQueue.push(queueIntegration(integration, integrationOptions))
    }

    // This waits until all integrations have been loaded, then will process all the callbacks for cross-integration communication.
    const initIntegrationManager = async () => {
        await Promise.all(integrationQueue)

        providers.forEach((provider) => {
            const { provides, ...providerDefault } = provider

            if (integrations[provides]) {
                // loop over providerDefault keys and values to make sure the integration has them if required
                Object.entries(providerDefault).forEach(([key, value]) => {
                    if (!integrations[provides][key]) {
                        integrations[provides][key] = value
                    }
                })
                return
            }

            const integration = Object.assign(createIntegration(provider), providerDefault)

            installIntegration(integration)
        })

        // Trigger callbacks that allow inter-integration communication
        integrationCallbacks.forEach((cb) => cb(integrations))
    }

    // Add a callback to an array so we can process those after the integrations have been loaded/initialized.
    const addIntegrationCallback = (cb) => {
        integrationCallbacks.push(cb)
    }

    // The integrationManager is in charge of loading the integrations and letting the app know when they have all been loaded/initialized.
    inject('integrationsManager', {
        add: addIntegration,
        init: initIntegrationManager,
        onReady: addIntegrationCallback,
        loaders,
    })

    // All integrations added to the app.
    inject('integrations', integrations)

    /**
     * Interal Functions
     */

    // Creates an integration instance
    const createIntegration = (integrationOptions) => {
        const { name, provides } = integrationOptions

        const integrationInstance = {
            name,
            provider: provides,
            key: provides || name,
            enabled: false,
        }

        return integrationInstance
    }

    // Takes an integrationInstance and options. Configures the integrationInstance based upon config and downloads the plugin if the integration has one.
    const queueIntegration = async (integrationInstance, integrationOptions) => {
        // ignoreDisable - The integration cannot be disabled via the url.
        // plugin - The plugin that is run if the integration is enabled.
        const { ignoreDisable, disableInPreview, disableInDev, plugin } = integrationOptions
        let integration = integrationInstance
        const queryResult = isEnabledViaQuery(integration.key, ignoreDisable)
        let isEnabled = $config.runtime.integrations[integration.name]?.enabled

        // enable/disable via query string
        if (queryResult !== null) isEnabled = queryResult
        // special pages may have some integrations disabled
        else if (isDisabledViaMeta(integration.key)) isEnabled = false
        // storyblok may have some integrations disabled for preview (like chat)
        else if (route.query._storyblok && disableInPreview === true) isEnabled = false
        // Dev trumps all, if we are in development and the integration has the disableInDev flag set,
        // then the integration will not be enabled
        if (isDev && disableInDev === true) isEnabled = false

        if (isEnabled) {
            integration.enabled = true

            if (plugin) {
                const cb = await plugin().then((m) => m.default || m)
                const cbPlugin = cb(ctx, inject) || {}
                integration = Object.assign(cbPlugin, integration)
            }
        }

        // If the integration is a provider and it is disabled then do not install it.
        if (integration.provider && !integration.enabled) return

        // Adds the integration.
        installIntegration(integration)
    }

    // Adds an integrationInstance to integrations dict.
    function installIntegration(integrationInstance) {
        const { key } = integrationInstance

        integrations[key] = integrationInstance
        // remove when everything flows through $integrations
        inject(key, integrationInstance)
    }

    // Checks the route to see if an integration should be disabled or not.
    const isDisabledViaMeta = (key) => {
        // the route we get is different than the route in components, this contains multiple mached values from children
        for (const index in route.meta) {
            const meta = route.meta[index]
            if (meta.disableIntegration && meta.disableIntegration.includes(key)) return true
        }

        return false
    }

    // enable/disable an integration via query string
    const isEnabledViaQuery = (key, ignoreDisable) => {
        const { query } = route

        if (query.enableIntegration && query.enableIntegration.includes(key)) return true
        if (
            query.disableIntegration &&
            !ignoreDisable &&
            (query.disableIntegration === 'all' || query.disableIntegration.includes(key))
        )
            return false

        return null
    }
}
